import { ensurePaymentMethodCard, ensureStripeCustomer } from './data';
import { createMoney, isArrayLength } from './genericHelpers';
import { EXTENDED_DATA_SCHEMA_TYPES } from './types';
import { types as sdkTypes } from '../util/sdkLoader';

export function getUserDetails(user) {
  // Combine first and last names using optional chaining and nullish coalescing
  const fullName = user?.attributes?.profile?.firstName
    ? `${user?.attributes?.profile?.firstName} ${user?.attributes?.profile?.lastName}`
    : user?.attributes?.profile?.publicData?.fullName;

  // Use optional chaining and nullish coalescing for profile image
  const profileImage =
    user?.profileImage?.attributes?.variants?.default?.url ||
    user?.profileImage?.attributes?.variants?.['square-small2x']?.url ||
    null; // Set to null if no image variants found

  // Use optional chaining and nullish coalescing for email
  const email = user?.attributes?.email || user?.attributes?.profile?.publicData?.email;

  // Extract ID using optional chaining and nullish coalescing
  const id = user?.id?.uuid;

  const firstName = user?.attributes?.profile?.firstName;
  const hasDefaultPaymentMethod =
    user?.id &&
    ensureStripeCustomer(user.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(user.stripeCustomer.defaultPaymentMethod).id;
  // Extract firstName using optional chaining (already defined earlier)
  const { completedAccountDetails, isVerified } = user?.attributes?.profile?.privateData || {};

  // Return object using destructuring assignment and computed property for clarity
  return {
    fullName,
    profileImage,
    email,
    id,
    firstName,
    hasDefaultPaymentMethod,
    completedAccountDetails,
    isVerified,
  };
}

export const getCompaniesAuthenticationKeys = details => {
  return (
    (isArrayLength(details?.companiesAuthentication) &&
      details?.companiesAuthentication?.map(item => item?.label).join(',')) ||
    ''
  );
};

export const getFacebookInfo = user => {
  if (!user || !user.id) return null;
  const { isConnectedWithFacebook, fullName, profilePicture, email } =
    user.attributes.profile.privateData?.facebookInfo || {};
  return {
    isConnectedWithFacebook,
    fullName,
    profilePicture,
    email,
  };
};
/**
 * Check if listingType has already been set.
 *
 * If listing type (incl. process & unitType) has been set, we won't allow change to it.
 * It's possible to make it editable, but it becomes somewhat complex to modify following panels,
 * for the different process. (E.g. adjusting stock vs booking availability settings,
 * if process has been changed for existing listing.)
 *
 * @param {Object} publicData JSON-like data stored to listing entity.
 * @returns object literal with to keys: { hasExistingListingType, existingListingTypeInfo }
 */
export const hasSetListingType = publicData => {
  const { listingType, transactionProcessAlias, unitType } = publicData;
  const existingListingTypeInfo = { listingType, transactionProcessAlias, unitType };

  return {
    hasExistingListingType: !!listingType && !!transactionProcessAlias && !!unitType,
    existingListingTypeInfo,
  };
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target listing type.
 *       These might exists if provider swaps between listing types before saving the draft listing.
 *
 * @param {Object} data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields from submitted data.
 */
export const pickListingFieldsData = (
  data,
  targetScope,
  targetListingType,
  listingFieldConfigs
) => {
  return listingFieldConfigs.reduce((fields, field) => {
    const { key, includeForListingTypes, scope = 'public', schemaType } = field || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetListingType =
      includeForListingTypes == null || includeForListingTypes.includes(targetListingType);

    if (isKnownSchemaType && isTargetScope && isTargetListingType) {
      const fieldValue = data[namespacedKey] || null;
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope && !isTargetListingType) {
      // Note: this clears extra custom fields
      // These might exists if provider swaps between listing types before saving the draft listing.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};
/**
 * Pick extended data fields from given extended data of the listing entity.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields
 */
const initialValuesForListingFields = (
  data,
  targetScope,
  targetListingType,
  listingFieldConfigs
) => {
  return listingFieldConfigs.reduce((fields, field) => {
    const { key, includeForListingTypes, scope = 'public', schemaType } = field || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetListingType =
      includeForListingTypes == null || includeForListingTypes.includes(targetListingType);

    if (isKnownSchemaType && isTargetScope && isTargetListingType) {
      const fieldValue = data[key] || null;
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * Get listing configuration. For existing listings, it is stored to publicData.
 * For new listings, the data needs to be figured out from listingTypes configuration.
 *
 * In the latter case, we select first type in the array. However, EditListingDetailsForm component
 * gets 'selectableListingTypes' prop, which it uses to provide a way to make selection,
 * if multiple listing types are available.
 *
 * @param {Array} listingTypes
 * @param {Object} existingListingTypeInfo
 * @returns an object containing mainly information that can be stored to publicData.
 */
export const getTransactionInfo = (
  listingTypes,
  existingListingTypeInfo = {},
  inlcudeLabel = false
) => {
  const { listingType, transactionProcessAlias, unitType } = existingListingTypeInfo;

  if (listingType && transactionProcessAlias && unitType) {
    return { listingType, transactionProcessAlias, unitType };
  } else if (listingTypes.length === 1) {
    const { listingType: type, label, transactionType } = listingTypes[0];
    const { alias, unitType: configUnitType } = transactionType;
    const labelMaybe = inlcudeLabel ? { label: label || type } : {};
    return {
      listingType: type,
      transactionProcessAlias: alias,
      unitType: configUnitType,
      ...labelMaybe,
    };
  }
  return {};
};
/**
 * Get initialValues for the form. This function includes
 * title, description, listingType, transactionProcessAlias, unitType,
 * and those publicData & privateData fields that are configured through
 * config.listing.listingFields.
 *
 * @param {object} props
 * @param {object} existingListingTypeInfo info saved to listing's publicData
 * @param {object} listingTypes app's configured types (presets for listings)
 * @param {object} listingFieldsConfig those extended data fields that are part of configurations
 * @returns initialValues object for the form
 */
export const getInitialValues = ({
  props,
  existingListingTypeInfo,
  listingTypes,
  listingFieldsConfig,
  currency,
  shippingPreferences,
  listing,
  existingUserTags,
}) => {
  const { description, title, publicData, privateData, price } = props?.listing?.attributes || {};
  const {
    listingType,
    shippingPriceInSubunitsOneItem,
    pickupEnabled,
    size,
    location,
    shippingEnabled,
    shippingPriceInSubunitsAdditionalItems,
    building,
    deliveryOptions,
    weight,
    massUnit,
    videos,
    domesticShippingCost: listingDomesticShippingCost,
    internationalShippingCost: listingInternationalShippingCost,
    auction,
    reserve,
    buying_format,
    fixedPrice,
    isFeaturedListing,
    authenticationId,
    authenticationType,
    isFreeDomesticShipping,
  } = publicData;
  const listingTags = getListingsTagData({ listing, existingUserTags });
  const tags = listingTags?.map(tag => tag?.name);
  const domesticShippingCost =
    listingDomesticShippingCost || shippingPreferences?.domesticShippingCost * 100;
  const internationalShippingCost =
    listingInternationalShippingCost || shippingPreferences?.internationalShippingCost * 100;

  const { reservePrice, startingPrice, auctionTime } = auction || {};
  // Initial values for the form
  return {
    auctionTime,
    title,
    description,
    price,
    pickupEnabled,
    size,
    location,
    tags,
    shippingEnabled,
    building,
    deliveryOptions,
    weight,
    massUnit,
    videos,
    reserve,
    buying_format,
    isFreeDomesticShipping,
    isFeaturedListing: isFeaturedListing ? [isFeaturedListing] : [],
    authenticationId,
    authenticationType,
    domesticShippingCost: createMoney(domesticShippingCost, currency),
    internationalShippingCost: createMoney(internationalShippingCost, currency),
    reservePrice: createMoney(reservePrice, currency),
    startingPrice: createMoney(startingPrice, currency),
    fixedPrice: createMoney(fixedPrice, currency),

    // Transaction type info: listingType, transactionProcessAlias, unitType
    ...getTransactionInfo(listingTypes, existingListingTypeInfo),
    ...initialValuesForListingFields(publicData, 'public', listingType, listingFieldsConfig),
    ...initialValuesForListingFields(privateData, 'private', listingType, listingFieldsConfig),
  };
};

export const getListingsTagData = ({ listing, existingUserTags }) => {
  if (!listing?.id) return null;
  const { tags = [] } = listing?.attributes?.publicData || {};

  return existingUserTags?.filter(tag => tags.some(t => t === tag?.id)) || [];
};

export const getTagsDataFromCurrentUser = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.tags || [];
};
// Function to merge listings based on common tagIds
export const mergeListingsByTagId = (listings, currentUserTags) => {
  const mergedListings = new Map();
  const unmatchedTags = new Set(currentUserTags.map(tag => tag.id));

  // Step 1: Collect listings by tagId
  listings.forEach(listing => {
    const { id, attributes } = listing;
    const { publicData } = attributes || {};
    const { tags } = publicData || {};

    if (Array.isArray(tags)) {
      tags.forEach(tagId => {
        if (!mergedListings.has(tagId)) {
          mergedListings.set(tagId, { listings: [], tagInfo: null });
        }
        const tagListings = mergedListings.get(tagId);
        const matchingTag = currentUserTags.find(tag => tag.id === tagId);
        if (matchingTag) {
          tagListings.listings.push({
            id,
            ...listing,
            tagName: matchingTag.name,
            tagId: matchingTag.id,
          });
          tagListings.tagInfo = matchingTag;

          // Remove the matching tag from unmatchedTags
          unmatchedTags.delete(tagId);
        }
      });
    }
  });

  // Step 2: Create the final array with a single entry per tag
  const mergedListingsArray = Array.from(mergedListings.values())
    .filter(({ tagInfo }) => tagInfo) // Filter out entries without tagInfo
    .map(({ listings, tagInfo }) => ({
      tagName: tagInfo.name,
      tagId: tagInfo.id,
      count: listings.length,
      listings,
    }));

  // Step 3: Include unmatchedTags in the final return value
  const unmatchedTagsArray = currentUserTags
    .filter(tag => unmatchedTags.has(tag.id))
    .map(tag => ({
      tagName: tag.name,
      tagId: tag.id,
      count: 0, // Unmatched tags have a count of 0
      listings: [],
    }));

  return [...mergedListingsArray, ...unmatchedTagsArray];
};

export const getEbayToken = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.privateData?.eBayToken || null;
};

export const getIsSelectedForDraft = listing => {
  // Return true if the listing is selected for draft, otherwise false
  return !!(listing?.id && listing?.attributes?.publicData?.isSelectedForDraft);
};

export const getUserShippingPreferences = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.privateData?.shippingPreferences || null;
};

export const getFeaturedListing = listing => {
  return !!(listing?.id && listing?.attributes?.publicData?.isFeaturedListing);
};

/**
 * Generates an array of image URLs using the base URL and an array of image objects.
 *
 * @param {Array} images - An array of image objects.
 * @param {string} url - The base URL for the images.
 * @returns {Array} An array of image URLs.
 * @throws {TypeError} If the `images` parameter is not an array.
 */
export const getImagesLink = (images, url) => {
  // Generate an array of image URLs using the base URL and the image object's URL attribute.
  const imageUrls =
    isArrayLength(images) &&
    images.map(image => {
      const imageUrl = `${url}${image?.attributes?.url}`;
      return imageUrl;
    });

  // Return the array of image URLs.
  return imageUrls;
};

export const getBlogData = blog => {
  // Get the base URL from the configuration.
  const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

  // Get the blog images and generate image URLs using the base URL.
  const blogImages = blog?.attributes?.images?.data || [];
  const imagesLink = blogImages?.length && getImagesLink(blogImages, baseUrl);

  // Get the blog title and description.
  const title = blog?.attributes?.title || '';
  const description = blog?.attributes?.description || '';
  const tags = blog?.attributes?.tags || '';
  const createdAt = blog?.attributes?.createdAt || '';

  // Return an object containing the blog title, description, and the first image URL (if available).
  return { title, description, tags, createdAt, image: imagesLink && imagesLink[0] };
};
